import * as amplitude from "@amplitude/analytics-browser";
import { useApolloClient } from "@apollo/client";
import { Button, Flex, MenuItem, Spinner, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import React from "react";
import { useNavigate } from "react-router-dom";
import { declineOffer } from "src/generated/hooks/form-service/requests";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { useOrganization } from "src/hooks/useOrganization";
import { refreshFormAdmissionsAfterMutation } from "src/scenes/parent/formTemplates/admissions";
import { useNextStep } from "src/scenes/parent/nextStep/NextStepProvider";
import * as Url from "src/services/url";
import {
  GET_OFFERS_FORM_CONTEXT,
  GET_PARENT_ASSIGNED_FORMS,
  GET_PARENT_FORMS_INDEX,
} from "../../graphql/queries";

interface Props {
  formId: uuid;
  offerId: uuid;
  school: string;
  isButton?: boolean;
  isDisabled?: boolean;
  onActionPendingChange?: (pending: boolean) => void;
}

export const DeclineOffer = ({
  formId,
  offerId,
  school,
  isButton = false,
  isDisabled = false,
  onActionPendingChange,
}: Props) => {
  const nextStep = useNextStep();
  const apolloClient = useApolloClient();
  const navigate = useNavigate();
  const organization = useOrganization();

  const toast = useAvelaToast({ position: "bottom" });
  const queryClient = useQueryClient();

  const [isLoading, setIsLoading] = React.useState(false);

  const { confirm, confirmationDialog, setBody, setHeader } =
    useConfirmationDialog({
      header: null,
      body: null,
      cancelButton: {
        label: "No, cancel",
      },
      confirmButton: {
        label: "Yes, decline",
        colorScheme: "red",
      },
      translate: true,
    });

  const handleOnClick = async (evt: React.MouseEvent) => {
    setHeader(<Text>Decline offer?</Text>);
    setBody(
      <Text>{`Declining the offer from ${school} can't be undone nor recovered.`}</Text>
    );

    if (await confirm()) {
      setIsLoading(true);
      onActionPendingChange?.(true);
      try {
        amplitude.track("Decline offer confirmed");
        const response = await declineOffer({
          path: { formId },
          body: { data: { offer_id: offerId } },
        });

        nextStep.process(response.data?.data.assignedForms);

        apolloClient.refetchQueries({
          include: [
            GET_OFFERS_FORM_CONTEXT,
            GET_PARENT_FORMS_INDEX,
            GET_PARENT_ASSIGNED_FORMS,
          ],
        });
        await refreshFormAdmissionsAfterMutation(queryClient, formId);

        toast({
          title: "Declined offer",
          status: "info",
        });
        navigate(organization.map(Url.Parent.index).withDefault("#"));
      } catch (err: any) {
        console.error(err);

        if ("error" in err && "httpStatus" in err.error) {
          const httpStatus = err.error.httpStatus;
          if (httpStatus === 400) {
            toast.error({
              title: "Offer has expired",
              description: "It can't be accepted or declined anymore",
            });
          } else if (httpStatus === 403) {
            toast.error({
              title: "You are not authorized to decline this offer",
              description: "You are not allowed to perform this action.",
            });
          } else if (httpStatus === 404) {
            toast.error({
              title: "Offer not found",
              description:
                "The offer you are trying to decline does not exist.",
            });
          } else {
            toast.genericError();
          }
        } else {
          toast.genericError();
        }
      } finally {
        setIsLoading(false);
        onActionPendingChange?.(false);
      }
    }
  };

  if (isButton) {
    return (
      <>
        <Button
          variant="outline"
          colorScheme="gray"
          size="sm"
          width="50%"
          onClick={(e) => {
            amplitude.track("Decline offer clicked", {
              button_location: "offers_page",
            });
            handleOnClick(e);
          }}
          isLoading={isLoading}
          isDisabled={isDisabled && !isLoading}
        >
          Decline
        </Button>
        {confirmationDialog}
      </>
    );
  }

  return (
    <>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={(e) => {
          amplitude.track("Decline offer clicked", {
            button_location: "overflow_menu",
          });
          handleOnClick(e);
        }}
        isDisabled={isDisabled && !isLoading}
      >
        <Flex align="center" gap={2}>
          {isLoading && <Spinner size="xs" flexShrink={0} />}
          Decline offer
        </Flex>
      </MenuItem>
      {confirmationDialog}
    </>
  );
};
